<script setup lang="ts">
/**
 * ModuleSlot — Single module slot (empty or filled)
 *
 * Empty: shows "Empty [slot type] slot" with dashed border, accepts drops and clicks
 * Filled: shows module icon, name, state controls, charge indicator, remove button
 * Clicking a filled slot also emits click-empty to open module selector (replace)
 */
import type { FitModule, SlotType, ModuleState } from '../../../shared/types';
import { iconUrl } from '../../composables/useApi';

const props = defineProps<{
  module: FitModule | null;
  slotType: SlotType;
  slotPosition: number;
}>();

const emit = defineEmits<{
  'click-empty': [slotType: SlotType, slotPosition: number];
  'set-state': [moduleId: number, state: ModuleState];
  'remove': [moduleId: number];
  'context-menu': [event: MouseEvent, module: FitModule];
  'drop': [slotType: SlotType, slotPosition: number, typeID: number, typeName: string];
}>();

function handleSlotClick(): void {
  // Clicking a filled slot opens the module selector to replace it
  emit('click-empty', props.slotType, props.slotPosition);
}

function setState(state: ModuleState): void {
  if (!props.module) return;
  emit('set-state', props.module.id, state);
}

function handleContextMenu(e: MouseEvent): void {
  if (props.module) {
    e.preventDefault();
    emit('context-menu', e, props.module);
  }
}

function handleDragOver(e: DragEvent): void {
  e.preventDefault();
  if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
}

function handleDrop(e: DragEvent): void {
  e.preventDefault();
  try {
    const data = e.dataTransfer?.getData('text/plain');
    if (!data) return;
    const parsed = JSON.parse(data);
    if (parsed.typeID) {
      emit('drop', props.slotType, props.slotPosition, parsed.typeID, parsed.typeName);
    }
  } catch { /* ignore */ }
}

function handleEmptyClick(): void {
  emit('click-empty', props.slotType, props.slotPosition);
}
</script>

<template>
  <div
    v-if="module"
    class="slot filled"
    :class="`state-${module.state}`"
    @contextmenu="handleContextMenu"
    @dragover="handleDragOver"
    @drop="handleDrop"
    @click="handleSlotClick"
  >
    <img :src="iconUrl(module.typeID, 64)" :alt="module.typeName" class="module-icon" />

    <div class="module-info">
      <span class="module-name" :title="module.typeName">{{ module.typeName }}</span>
      <span v-if="module.chargeName" class="charge-name" :title="module.chargeName">
        <img v-if="module.chargeTypeID" :src="iconUrl(module.chargeTypeID, 32)" class="charge-icon" />
        {{ module.chargeName }}
      </span>
    </div>

    <!-- Module control buttons (power / activate / overheat) -->
    <div class="module-controls">
      <button
        class="mod-ctrl-btn power"
        :class="module.state !== 'offline' ? 'on' : 'off'"
        title="Toggle online/offline"
        @click.stop="setState(module.state === 'offline' ? 'online' : 'offline')"
      >
        <svg width="10" height="10" viewBox="0 0 10 10"><circle cx="5" cy="5" r="4" fill="none" stroke="currentColor" stroke-width="1.2"/><line x1="5" y1="1" x2="5" y2="5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>
      </button>
      <button
        v-if="module.canActivate"
        class="mod-ctrl-btn activate"
        :class="{ active: module.state === 'active' || module.state === 'overheated' }"
        :disabled="module.state === 'offline'"
        title="Toggle active"
        @click.stop="setState(module.state === 'active' || module.state === 'overheated' ? 'online' : 'active')"
      >
        <svg width="10" height="10" viewBox="0 0 10 10"><polygon points="2,1 9,5 2,9" fill="currentColor"/></svg>
      </button>
      <button
        v-if="module.canOverheat"
        class="mod-ctrl-btn overheat"
        :class="{ active: module.state === 'overheated' }"
        :disabled="module.state === 'offline'"
        title="Toggle overheat"
        @click.stop="setState(module.state === 'overheated' ? 'active' : 'overheated')"
      >
        <svg width="10" height="10" viewBox="0 0 10 10"><path d="M5 1 L6.5 4 L9 5 L6.5 6 L5 9 L3.5 6 L1 5 L3.5 4 Z" fill="currentColor"/></svg>
      </button>
    </div>

    <button
      class="remove-btn"
      title="Remove module"
      @click.stop="emit('remove', module.id)"
    >
      <svg width="12" height="12" viewBox="0 0 12 12">
        <line x1="3" y1="3" x2="9" y2="9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
        <line x1="9" y1="3" x2="3" y2="9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
      </svg>
    </button>
  </div>

  <div
    v-else
    class="slot empty"
    @click="handleEmptyClick"
    @dragover="handleDragOver"
    @drop="handleDrop"
  >
    <span class="empty-label">Empty {{ slotType }} slot</span>
  </div>
</template>

<style scoped>
.slot {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border-radius: var(--radius-sm);
  min-height: 44px;
  transition: background var(--transition-fast);
}

/* Filled slot */
.filled {
  background: linear-gradient(180deg, var(--bg-secondary), var(--bg-panel));
  border: 1px solid var(--border-color);
  border-left: 3px solid var(--border-color);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.018);
  cursor: pointer;
}

.filled:hover {
  background: linear-gradient(180deg, var(--bg-hover), var(--bg-secondary));
}

.state-offline { border-left-color: var(--state-offline); opacity: 0.6; }
.state-online { border-left-color: var(--state-online); }
.state-active { border-left-color: var(--state-active); box-shadow: inset 0 0 10px rgba(76, 175, 80, 0.1); }
.state-overheated {
  border-left-color: var(--state-overheated);
  animation: overheat-row-pulse 1.5s ease-in-out infinite;
}

@keyframes overheat-row-pulse {
  0%, 100% { box-shadow: inset 0 0 10px rgba(244, 67, 54, 0.1); }
  50% { box-shadow: inset 0 0 20px rgba(244, 67, 54, 0.25); }
}

.state-offline img { filter: grayscale(70%); }

.module-icon {
  width: 32px;
  height: 32px;
  border-radius: 3px;
  flex-shrink: 0;
}

.module-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.module-name {
  font-size: 0.82rem;
  color: var(--text-primary);
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;
}

.charge-name {
  font-size: 0.72rem;
  color: var(--accent-orange);
  font-family: var(--font-mono);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
  display: flex;
  align-items: center;
  gap: 4px;
}

.charge-icon {
  width: 16px;
  height: 16px;
  border-radius: 2px;
}

/* Module control buttons — matches old PHP site */
.module-controls {
  display: flex;
  gap: 3px;
  margin-left: auto;
  opacity: 0.7;
  transition: opacity var(--transition-fast);
  flex-shrink: 0;
}

.filled:hover .module-controls { opacity: 1; }

.mod-ctrl-btn {
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  background: var(--bg-input);
  color: var(--text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-fast);
}

.mod-ctrl-btn:hover:not(:disabled) {
  background: linear-gradient(180deg, var(--bg-hover), var(--bg-secondary));
  color: var(--text-primary);
  transform: scale(1.1);
}

.mod-ctrl-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.mod-ctrl-btn.power.on {
  color: var(--accent-green);
  border-color: var(--accent-green);
}

.mod-ctrl-btn.power.off {
  color: var(--text-secondary);
  border-color: var(--text-secondary);
}

.mod-ctrl-btn.activate.active {
  color: var(--accent-green);
  border-color: var(--accent-green);
  background: rgba(76, 175, 80, 0.2);
  box-shadow: 0 0 6px rgba(76, 175, 80, 0.4);
}

.mod-ctrl-btn.overheat.active {
  color: var(--accent-red);
  border-color: var(--accent-red);
  background: rgba(244, 67, 54, 0.2);
  box-shadow: 0 0 6px rgba(244, 67, 54, 0.4);
  animation: overheat-btn-pulse 1.5s ease-in-out infinite;
}

@keyframes overheat-btn-pulse {
  0%, 100% { box-shadow: 0 0 4px rgba(244, 67, 54, 0.3); }
  50% { box-shadow: 0 0 10px rgba(244, 67, 54, 0.6); }
}

/* Remove button */
.remove-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  border-radius: var(--radius-sm);
  opacity: 0;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.filled:hover .remove-btn { opacity: 1; }

.remove-btn:hover {
  background: rgba(244, 67, 54, 0.2);
  color: var(--accent-red);
}

/* Empty slot */
.empty {
  border: 1px dashed var(--border-color);
  cursor: pointer;
  background: rgba(0, 0, 0, 0.1);
  justify-content: center;
}

.empty:hover {
  border-color: var(--accent-blue);
  background: rgba(0, 212, 255, 0.045);
}

.empty-label {
  font-family: var(--font-mono);
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-secondary);
}
</style>
